import { FC, useState } from 'react'
import { useCreateUser } from '@hooks/useUsers'
import { useTranslation } from 'react-i18next'
import { toast } from '@lib/toasts'
import { Input } from 'components/form/input'
import { ModalForm } from 'components/notifications'
import { AxiosError } from 'axios'
import { ErrorBanner } from './ErrorBanner'
import { Field } from 'components/form/field'

interface Props {
  onCancel: () => void
  onSave: () => void
}

export const NewUserForm: FC<Props> = ({ onCancel, onSave }) => {
  const { t } = useTranslation()
  const createUser = useCreateUser()
  const [email, setEmail] = useState<string>('')
  const [errorResponseCode, setErrorResponseCode] = useState<number>()
  const [dirty, setDirty] = useState(false)
  const [clientError, setClientError] = useState<string | undefined>(
    t('features.usersAdmin.emailIsRequired'),
  )

  const validateEmail = (email: string) => {
    const normalized = email.trim()

    if (normalized === '') {
      setClientError(t('features.usersAdmin.emailIsRequired'))
      return
    }

    // very simple regex for email validation
    if (!/^.+@.+\..+$/.test(normalized)) {
      setClientError(t('features.usersAdmin.invalidEmailAddress'))
      return
    }

    setClientError(undefined)
  }

  const save = async (email: string) => {
    setDirty(true)
    if (clientError) return

    try {
      await createUser.mutateAsync({ email })

      onSave()

      toast({
        title: t('features.usersAdmin.userCreated'),
        variant: 'success',
        content: t('features.usersAdmin.successfullyCreatedUser', { email }),
      })
      setErrorResponseCode(undefined)
      setDirty(false)
    } catch (error) {
      if (error instanceof AxiosError) {
        setErrorResponseCode(error.response?.status)
      } else {
        toast({
          title: t('features.usersAdmin.unableToCreateUser'),
          variant: 'error',
          content: t('features.usersAdmin.failedToCreateUser'),
        })
      }
    }
  }

  const formFields = (
    <Field
      label={t('features.usersAdmin.newUserEmailAddress')}
      htmlFor="email"
      errors={dirty && clientError ? [clientError] : []}
    >
      {(id) => (
        <Input
          id={id}
          autoFocus
          onChange={(event) => {
            const email = event.currentTarget.value
            validateEmail(email)
            setEmail(email)
          }}
          value={email}
        />
      )}
    </Field>
  )

  return (
    <ModalForm
      fields={formFields}
      onCancel={onCancel}
      onSave={() => void save(email)}
      isSaving={createUser.isLoading}
      saveButtonText={t('features.usersAdmin.createUser')}
      saveDisabled={dirty && Boolean(clientError)}
    >
      <ErrorBanner email={email} status={errorResponseCode} />
    </ModalForm>
  )
}
